import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data.user);
    } catch (err) {
      // Only clear token if server explicitly says unauthorized (401)
      // Do NOT clear on network errors, timeouts, or server errors (500)
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setUser(null);
      }
      // For other errors (network, 500, etc.) keep the token and user state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Listen for forced logout from API interceptor
  useEffect(() => {
    const handleForcedLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth-logout', handleForcedLogout);
    return () => window.removeEventListener('auth-logout', handleForcedLogout);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    if (res.data.user) {
      setUser(res.data.user);
    }
    return res.data;
  };

  const logout = () => {
    const request = authAPI.logout().catch(() => {});
    setUser(null);
    return request;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
